<template>
  <v-card flat fill-height v-resize="onResize">
    <msg-detail
      v-show="parseInt($route.params.id) && $route.params.folder === 'detail'"
      @xfer-msg="$emit('xfer-msg', $event)"
      @del-msg="$emit('del-msg', $event)"
      @move-to-custom-folder="$emit('move-to-custom-folder', $event)"
      @remove-from-custom-folder="$emit('remove-from-custom-folder', $event)"
    />
    <v-skeleton-loader
      v-show="$route.params.folder !== 'detail'"
      :loading="initialLoading"
      class="pa-0"
      type="table-heading, list-item-avatar@18"
      :types="{'list-item-avatar': 'avatar, table-cell, table-cell, table-cell, text'}"
      :style="skeletonStyle()"
    >
      <msgs-header />
      <v-data-table
          ref="datatable"
          :class="isOnMobile ? 'px-0' : 'px-2'"
          v-model="selection"
          :show-select="showSelect"
          :loading="loading"
          :loading-text="$t('vmail.loading')"
          :headers="headers"
          :hide-default-header="!showHeader"
          :no-data-text="$t('vmail.tip.noData')"
          :options.sync="pagination"
          :server-items-length="vmPagination.total"
          :items-per-page="itemPerPage"
          :footer-props="footerProps"
          :disable-sort="true"
          :items="voicemails"
          :max-width="1000"
          mobile-breakpoint="0"
          :height="tableHeight"
          data-testid="voicemails"
          :item-class="getRowClass"
          @click:row="(event, { item }) => onRowClick(item)"
          @mousedown:row="(event, { item }) => startPress(item)"
          @touchstart:row="(event, { item }) => startPress(item)"
          @mouseup:row="cancelPress"
          @touchend:row="cancelPress"
        >
          <!-- Custom cells -->
          <template v-slot:item.favorite="{ item }">
            <favorite-col :voicemail="item" />
          </template>
          <template v-slot:item.callerName="{ item }">
            <caller-name-col :voicemail="item" :usedInList="true" />
          </template>
          <template v-slot:item.origtime="{ item }">
            <origtime-col :voicemail="item" />
          </template>
          <template v-slot:item.transcription="{ item }">
            <transcription-col :voicemail="item" />
          </template>
          <template v-slot:header.favorite>
            <bulk-btn
              v-if="canWriteVMsg && selection.length"
              :items="selection"
              @bulk-mark-read="$emit('bulk-mark-read', $event)"
              @bulk-mark-unread="$emit('bulk-mark-unread', $event)"
              @bulk-xfer-msg="$emit('bulk-xfer-msg', $event)"
              @bulk-favorite="$emit('bulk-favorite', $event)"
              @bulk-unfavorite="$emit('bulk-unfavorite', $event)"
              @bulk-del-msg="$emit('bulk-del-msg', $event)"
              @bulk-move-to-custom-folder="$emit('bulk-move-to-custom-folder', $event)"
              @bulk-remove-from-custom-folder="$emit('bulk-remove-from-custom-folder', $event)" />
            <v-tooltip top open-on-hover :open-delay="500" v-else>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="reloadPage()"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('vmail.tip.refreshVm') }}</span>
            </v-tooltip>
          </template>
          <!-- Custom Footer -->
          <template v-slot:footer.prepend>
            <v-pagination
              v-if="!isOnMobile && vmPagination.pages > 1"
              v-model="currentPage"
              :length="vmPagination.pages"
              :total-visible="6"
            />
          </template>
        </v-data-table>
      </v-skeleton-loader>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BulkBtn from '@/components/voicemails/msgs_table/bulk_btn'
import MsgsHeader from '@/components/voicemails/msgs_table/msgs_header'
import MsgDetail from '@/components/voicemails/msg_detail/index'
import CallerNameCol from '@/components/voicemails/msgs_table/caller_name_col'
import OrigtimeCol from '@/components/voicemails/msgs_table/origtime_col'
import FavoriteCol from '@/components/voicemails/msgs_table/favorite_col'
import TranscriptionCol from '@/components/voicemails/msgs_table/transcription_col'

export default {
  name: 'voicemail-msgs-table',
  components: { MsgsHeader, BulkBtn, MsgDetail, CallerNameCol, FavoriteCol, OrigtimeCol, TranscriptionCol },
  data () {
    return {
      loading: false,
      pagination: {
        page: 1
      },
      selection: [],
      tableHeight: 0,
      pressTimer: null,
      showSelect: true,
      showHeader: true
    }
  },
  computed: {
    ...mapGetters({
      voicemailsList: 'voicemails/messages',
      initialLoading: 'voicemails/initialLoading',
      vmPagination: 'voicemails/pagination',
      canWriteVMsg: 'permissions/vmailsWrite',
      searchText: 'search/searchText'
    }),
    headers () {
      const headers = []
      if (this.isOnMobile) {
        headers.push(
          { text: '', value: 'data-table-select', width: '0%' },
          { text: '', value: 'callerName', width: '72%' },
          { text: '', value: 'origtime', width: '100px' },
          { text: '', value: 'favorite', width: '10px' }
        )
      } else {
        headers.push(
          { text: '', value: 'data-table-select', width: '0%' },
          { text: '', value: 'favorite', width: '0%' },
          { text: '', value: 'callerName', width: '10%' },
          { text: '', value: 'origtime', width: '13%' },
          { text: '', value: 'transcription', width: '72%' }
        )
      }
      return headers
    },
    footerProps () {
      return {
        'items-per-page-options': [5, 10, 15, 18, 20, 25, 50, 100],
        pageText: this.$t('vmail.footerProps.pageText', [this.vmPagination?.start, this.vmPagination?.perPage, this.vmPagination?.total]),
        'items-per-page-text': this.$t('vmail.footerProps.perPage')
      }
    },
    voicemails () {
      return this.voicemailsList.filter(msg => msg?.id > 0)
    },
    currentPage: {
      get () { return this.vmPagination.curPage },
      set (page) { this.pagination.page = page }
    },
    itemPerPage () {
      return this.vmPagination?.perPage || 18
    },
    isDark () { return this.$vuetify.theme.dark },
    isOnMobile () { return this.$vuetify.breakpoint.mobile }
  },
  watch: {
    'pagination.itemsPerPage': {
      async handler (newItemsPerPage, oldItemsPerPage) {
        if (newItemsPerPage !== oldItemsPerPage) {
          this.loading = true
          this.setLimitPerPage(newItemsPerPage)
          await this.reloadPage()
          this.loading = false
        }
      },
      deep: true
    },
    'pagination.page': {
      async handler (newPage, oldPage) {
        if (newPage !== oldPage) {
          this.loading = true
          this.setPage(newPage)
          await this.reloadPage()
          this.loading = false
        }
      },
      deep: true
    },
    async searchText () {
      await this.reloadPage()
    },
    voicemails () {
      this.resetSelection()
    },
    isOnMobile (newValue) {
      this.showSelect = !newValue
      this.showHeader = !newValue
    },
    selection (newValue) {
      if (this.isOnMobile && this.selection.length === 0) {
        this.showSelect = false
        this.showHeader = false
      }
    }
  },
  methods: {
    ...mapActions({
      callNumber: 'callNumber',
      toggleRead: 'voicemails/toggleRead',
      setPage: 'voicemails/setPage',
      setLimitPerPage: 'voicemails/setLimitPerPage',
      reloadPage: 'voicemails/reloadPage'
    }),
    skeletonStyle () {
      if (this.isOnMobile) {
        return {
          height: 'calc(100vh - 55px)',
          overflowY: 'auto'
        }
      } else {
        return {
          height: 'calc(100vh - 25px - 55px)',
          overflowY: 'auto'
        }
      }
    },
    async resetSelection () {
      this.selection = []
    },
    onResize () {
      const footerHeight = document.querySelector('footer') || 50
      this.tableHeight = footerHeight ? window.innerHeight - 207 : 700
    },
    onRowClick (voicemail) {
      if (this.isOnMobile && this.selection.length > 0) {
        const index = this.selection.findIndex(item => item.id === voicemail.id)
        if (index !== -1) {
          this.selection.splice(index, 1)
        } else {
          this.selection.push(voicemail)
        }
      } else {
        this.openDetail(voicemail)
      }
    },
    openDetail (voicemail) {
      this.$router.push({ name: 'voicemails', params: { folder: 'detail', id: voicemail.id } })
    },
    getRowClass (voicemail) {
      if (this.selection.length === 0) {
        const readClass = this.isDark ? 'grey darken-3 clickable-row' : 'grey lighten-4 clickable-row'
        return voicemail.isRead ? readClass : 'clickable-row'
      } else {
        return ''
      }
    },
    startPress (voicemail) {
      this.cancelPress()
      if (!voicemail) return
      this.pressTimer = setTimeout(() => {
        this.showSelect = true
        this.showHeader = true
        this.selection.push(voicemail)
      }, 600)
    },
    cancelPress () {
      clearTimeout(this.pressTimer)
    }
  }
}
</script>

<style>
  .v-data-table th.v-data-table__checkbox,
  .v-data-table td.v-data-table__checkbox {
    width: 0%;
    min-width: 0%;
    max-width: 0%;
  }

  .clickable-row {
    cursor: pointer;
  }
  /* Remove the row separators */
  .v-data-table > .v-data-table__wrapper > table > tbody > tr {
    border-bottom: none !important;  /* Ensures no bottom borders */
  }

  /* Remove the column separators */
  .v-data-table td,
  .v-data-table th {
    border-bottom: none !important;  /* Removes row dividers */
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
  }

  /* Remove all table borders */
  .v-data-table > .v-data-table__wrapper > table {
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  /* Remove the shadow around the table */
  .v-data-table {
    box-shadow: none !important;
    border: none !important;
  }

  /* Reduce padding */
  .v-data-table td,
  .v-data-table th {
    padding: 8px !important;
  }

  .cell-content {
    display: -webkit-box;
    flex-direction: column;
    line-height: 1.8;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .primary-text {
    font-size: 1.0em;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .sub-text {
    font-size: 1.0em;
    color: gray;
  }
</style>
